import { useEffect, useState, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { Plus, Grid3X3, Table2, Video } from 'lucide-react';
import { useStreamsStore } from '@/lib/stores/streams';
import { useAuthStore } from '@/lib/stores/auth';
import { canAccessFeature, type RoleName } from '@/lib/utils/role-access';
import { StreamCard } from '@/components/streams/stream-card';
import { StreamDetailsSheet } from '@/components/streams/stream-details-sheet';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useDurableObjectChannel } from '@/hooks/use-durable-object-channel';
import FilterBar from '@/components/common/filter-bar';
import { useSearchParams } from 'react-router-dom';
import VirtualizedGrid from '@/components/common/virtualized-grid';
import { STREAM_FILTERS } from '@/lib/shared-types/filters';
import { PageHeader } from '@/components/page/page-header';
import { PageSection } from '@/components/page/page-section';
import { PageToolbar } from '@/components/page/page-toolbar';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { MediaImage } from '@/components/ui/media-image';
import type { Stream } from '@/lib/stores/streams/types';

export default function StreamsPage() {
  const {
    streams,
    isLoading,
    filters,
    fetchStreams,
    setFilters,
    pagination
  } = useStreamsStore();
  const { user } = useAuthStore();
  const [searchParams] = useSearchParams();

  // Custom hook to handle load more functionality
  const { loadMoreStreams } = useStreamsStore();
  const loadMoreRef = useRef(loadMoreStreams);

  // Update ref when loadMoreStreams changes
  useEffect(() => {
    loadMoreRef.current = loadMoreStreams;
  }, [loadMoreStreams]);
  const canManageStreams = canAccessFeature('create_stream', user?.role?.name as RoleName);
  const [viewMode, setViewMode] = useState<'table' | 'grid'>('table');
  const [selectedStream, setSelectedStream] = useState<Stream | null>(null);
  const previousStreamsRef = useRef<typeof streams>([]);

  // Sync URL search params to store filters on mount and when URL changes
  useEffect(() => {
    const urlFilters: Record<string, string | string[]> = {};
    searchParams.forEach((value, key) => {
      // Handle comma-separated values for multiselect
      if (value.includes(',')) {
        urlFilters[key] = value.split(',');
      } else {
        urlFilters[key] = value;
      }
    });
    if (Object.keys(urlFilters).length > 0) {
      setFilters(urlFilters);
    }
  }, [searchParams, setFilters]);

  // Fetch streams when filters change
  useEffect(() => {
    fetchStreams();
  }, [fetchStreams, filters]);

  // Real-time WebSocket subscription for stream list updates
  // Memoize the message handler to prevent connection leaks
  const handleStreamListMessage = useCallback((message: unknown) => {
    if (!message || typeof message !== 'object') {
      return;
    }

    const event = message as { type?: string; stream?: Stream };
    if (event.type === 'stream_status_changed' || event.type === 'stream_list_update') {
      const stream = event.stream;

      // Check if this is a status change from scheduled to live
      if (stream && stream.status === 'live') {
        const previousStream = previousStreamsRef.current.find(s => s.id === stream.id);
        if (previousStream && previousStream.status === 'scheduled') {
          toast.success(`${stream.title || 'A stream'} is now live!`, {
            description: 'The stream has started broadcasting',
            duration: 5000,
          });
        }
      }

      // Refresh streams list to get updated data
      fetchStreams();
    }
  }, [fetchStreams]);

  useDurableObjectChannel({
    channel: 'streams:list',
    enabled: true,
    onMessage: handleStreamListMessage,
  });

  // Update previous streams ref when streams change
  useEffect(() => {
    previousStreamsRef.current = streams;
  }, [streams]);

  // Use shared filter configuration
  const filterConfigs = STREAM_FILTERS;

  // Handle filter changes from FilterBar
  const handleFilterChange = (newFilters: Record<string, string | string[]>) => {
    setFilters(newFilters);
  };

  if (isLoading && streams.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'live':
        return <Badge variant="destructive" className="uppercase">Live</Badge>;
      case 'scheduled':
        return <Badge variant="secondary" className="uppercase">Scheduled</Badge>;
      case 'ended':
        return <Badge variant="outline" className="uppercase">Ended</Badge>;
      default:
        return <Badge variant="outline" className="uppercase">{status}</Badge>;
    }
  };

  const getStreamPrimaryAction = (stream: Stream) => {
    if (stream.status === 'live') return 'Watch Live';
    if (stream.status === 'scheduled') return 'View Details';
    return 'View Recording';
  };

  return (
    <div className="@container/streams mx-auto w-full max-w-[1800px] space-y-4 px-3 py-4 @[740px]/streams:space-y-5 @[740px]/streams:px-4 @[740px]/streams:py-5 @[1100px]/streams:space-y-6 @[1100px]/streams:px-6 @[1100px]/streams:py-6">
      <PageHeader
        title="Streams"
        actions={canManageStreams ? (
          <Button asChild size="sm">
            <Link to="/admin/streams/new">
              <Plus className="h-4 w-4 mr-2" />
              Create Stream
            </Link>
          </Button>
        ) : undefined}
      />

      <div className="@container/page-toolbar">
        <PageToolbar
          leading={(
            <FilterBar
              filters={filterConfigs}
              activeFilters={filters as Record<string, string | string[]>}
              onFilterChange={handleFilterChange}
            />
          )}
        />
      </div>

      <PageSection
        title="All Streams"
        className="border-border/70"
        contentClassName="p-3 sm:p-4"
        actions={(
          <div className="flex items-center gap-1 rounded-lg border border-border/70 bg-muted/30 p-1">
            <Button
              variant={viewMode === 'table' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('table')}
              aria-label="Table view"
            >
              <Table2 className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === 'grid' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('grid')}
              aria-label="Grid view"
            >
              <Grid3X3 className="h-4 w-4" />
            </Button>
          </div>
        )}
      >
        {viewMode === 'table' ? (
          <div className="space-y-3">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Stream</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Scheduled</TableHead>
                    <TableHead>Viewers</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {streams.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="h-20 text-center text-muted-foreground">
                        {filters.status || filters.searchQuery
                          ? 'Try adjusting your filters or search terms'
                          : 'No streams are currently available'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    streams
                      .filter((stream) => stream && stream.id && !isNaN(stream.id) && stream.id > 0)
                      .map((stream) => (
                        <TableRow key={stream.id}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="h-10 w-16 shrink-0 overflow-hidden rounded bg-slate-900">
                                {stream.thumbnailUrl ? (
                                  <MediaImage src={stream.thumbnailUrl} alt={stream.title} className="h-full w-full object-cover" />
                                ) : (
                                  <div className="flex h-full items-center justify-center text-slate-600">
                                    <Video className="h-4 w-4" />
                                  </div>
                                )}
                              </div>
                              <div className="min-w-0">
                                <button
                                  type="button"
                                  onClick={() => setSelectedStream(stream)}
                                  className="truncate text-left font-medium text-primary hover:underline"
                                >
                                  {stream.title}
                                </button>
                                <p className="text-xs text-muted-foreground">
                                  {stream.creator?.name || `Creator #${stream.createdBy}`}
                                </p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>{getStatusBadge(stream.status)}</TableCell>
                          <TableCell className="text-muted-foreground">
                            {stream.scheduledStart ? new Date(stream.scheduledStart).toLocaleString() : 'Not scheduled'}
                          </TableCell>
                          <TableCell>{stream.viewerCount || 0}</TableCell>
                          <TableCell>
                            <div className="flex justify-end">
                              <Button asChild variant="outline" size="sm">
                                <Link to={`/streams/${stream.id}`}>{getStreamPrimaryAction(stream)}</Link>
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                  )}
                </TableBody>
              </Table>
            </div>

            {pagination.hasMore && (
              <div className="flex justify-center">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (!isLoading) {
                      loadMoreRef.current();
                    }
                  }}
                  disabled={isLoading}
                >
                  {isLoading ? 'Loading...' : 'Load more'}
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="min-h-[600px]">
            <VirtualizedGrid
              items={streams.filter((stream) => stream && stream.id && !isNaN(stream.id) && stream.id > 0)}
              renderItem={(stream) => (
                <StreamCard
                  stream={stream}
                  onDetailsClick={setSelectedStream}
                />
              )}
              keyExtractor={(stream) => stream.id.toString()}
              columns={{ sm: 1, md: 2, lg: 3, xl: 4, '2xl': 5 }}
              gap="gap-4"
              loading={isLoading}
              hasNextPage={pagination.hasMore}
              onLoadNextPage={() => {
                if (!isLoading && pagination.hasMore) {
                  loadMoreRef.current();
                }
              }}
              loadMoreThreshold={5}
              viewMode="grid"
              emptyMessage={
                filters.status || filters.searchQuery
                  ? 'Try adjusting your filters or search terms'
                  : 'No streams are currently available'
              }
            />
          </div>
        )}
      </PageSection>

      {/* Stream Details Sheet */}
      {selectedStream && (
        <StreamDetailsSheet
          stream={selectedStream}
          open={!!selectedStream}
          onOpenChange={(open) => !open && setSelectedStream(null)}
        />
      )}
    </div>
  );
}
